import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react'

const theseusLogo = '/theseus-logo.png'

type SessionState = 'checking' | 'valid' | 'invalid'

export function ResetPasswordForm() {
  const { updatePassword } = useAuth()
  const navigate = useNavigate()
  const [sessionState, setSessionState] = useState<SessionState>('checking')
  const [success, setSuccess] = useState(false)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const confirmPasswordRef = useRef<HTMLInputElement>(null)
  const sessionValidatedRef = useRef(false)
  // Captured during render, before the effect below strips it from the URL.
  // Reading it inside the effect would break under StrictMode: the second pass
  // finds the URL already cleaned and falls through to the no-session branch.
  //
  // Deliberately has no setter. Clearing it would re-run the effect below into
  // its no-session branch, and that getSession() can resolve after
  // updatePassword's global sign-out — reporting "invalid" over a password
  // change that actually succeeded. Consumption is tracked in a ref instead,
  // which triggers no render.
  const [pendingToken] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get('token_hash')
  )
  const tokenConsumedRef = useRef(false)

  useEffect(() => {
    // Deliberately not verified here. A scanner prefetching this page would
    // spend the token before the recipient ever arrives, which is the exact
    // failure this flow exists to prevent. Verification happens on submit.
    if (pendingToken) {
      // Show the form with no session yet; submitting establishes one.
      setSessionState('valid')
      // Drop the token from the URL so it stays out of history, referrers and
      // analytics. The value is already held in state.
      window.history.replaceState(null, '', window.location.pathname)
      return
    }

    const hasRecoveryToken = window.location.hash.includes('type=recovery')

    if (hasRecoveryToken) {
      // Wait for Supabase to process the token
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        if (event === 'PASSWORD_RECOVERY' && session) {
          sessionValidatedRef.current = true
          setSessionState('valid')
          // Clear the URL hash
          window.history.replaceState(null, '', window.location.pathname)
        }
      })

      // Timeout fallback if event doesn't fire
      // Use ref instead of state to avoid stale closure - the callback captures
      // sessionState from the initial render, but ref.current is always current
      const timeout = setTimeout(() => {
        if (!sessionValidatedRef.current) {
          setSessionState('invalid')
        }
      }, 5000)

      return () => {
        subscription.unsubscribe()
        clearTimeout(timeout)
      }
    } else {
      // No token in URL - check if there's already a valid session
      supabase.auth.getSession().then(({ data: { session } }) => {
        setSessionState(session ? 'valid' : 'invalid')
      })
    }
  }, [pendingToken])

  const canSubmit = password.trim() !== '' && confirmPassword.trim() !== ''

  const handleSubmit = async () => {
    setError(null)

    if (!password || !confirmPassword) {
      setError('Please fill in both password fields')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    setSubmitting(true)

    try {
      // Spend the token here rather than on page load, so it is consumed by a
      // person completing the form and not by whatever fetched the URL first.
      if (pendingToken && !tokenConsumedRef.current) {
        const { error: verifyError } = await supabase.auth.verifyOtp({
          token_hash: pendingToken,
          type: 'recovery',
        })
        if (verifyError) {
          setError(
            'This password reset link is invalid or has expired. Please request a new password reset email.'
          )
          return
        }
        // A retry after a later failure must not re-verify a spent token.
        tokenConsumedRef.current = true
      }

      await updatePassword(password)
      setSuccess(true)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update password'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleContinue = () => {
    // Already signed out globally after password update
    navigate('/login')
  }

  const focusConfirmPassword = () => {
    confirmPasswordRef.current?.focus()
  }

  // Loading state
  if (sessionState === 'checking') {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <a
          href="https://theseus.us"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed top-4 left-4 md:top-6 md:left-6 hover:opacity-80 transition-opacity z-10"
        >
          <img src={theseusLogo} alt="Theseus" className="h-5 md:h-6" />
        </a>

        <main className="flex-1 flex items-center justify-center px-6 py-16">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              Verifying...
            </span>
          </div>
        </main>
      </div>
    )
  }

  // Invalid/missing session state. Success is terminal and outranks it: the
  // password change has already happened, and updatePassword signs out
  // globally, so a later session check legitimately finds nothing.
  if (sessionState === 'invalid' && !success) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <a
          href="https://theseus.us"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed top-4 left-4 md:top-6 md:left-6 hover:opacity-80 transition-opacity z-10"
        >
          <img src={theseusLogo} alt="Theseus" className="h-5 md:h-6" />
        </a>

        <main className="flex-1 flex items-center justify-center px-6 py-16">
          <div className="w-full max-w-md wizard-fade-in">
            <div className="mb-10">
              <h1 className="text-2xl text-foreground">
                Invalid reset link
              </h1>
              <p className="text-muted-foreground mt-2">
                This password reset link is invalid or has expired. Please request a new password reset email.
              </p>
            </div>

            <div className="pt-4">
              <button
                onClick={() => navigate('/login')}
                className="w-full px-6 py-2.5 rounded-md text-sm font-medium transition-all
                  bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Go to sign in
              </button>
            </div>
          </div>
        </main>
      </div>
    )
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <a
          href="https://theseus.us"
          target="_blank"
          rel="noopener noreferrer"
          className="fixed top-4 left-4 md:top-6 md:left-6 hover:opacity-80 transition-opacity z-10"
        >
          <img src={theseusLogo} alt="Theseus" className="h-5 md:h-6" />
        </a>

        <main className="flex-1 flex items-center justify-center px-6 py-16">
          <div className="w-full max-w-md wizard-fade-in">
            <div className="mb-10">
              <h1 className="text-2xl text-foreground">
                Password updated
              </h1>
              <p className="text-muted-foreground mt-2">
                Your password has been changed successfully.
              </p>
            </div>

            <div className="pt-4">
              <button
                onClick={handleContinue}
                className="w-full px-6 py-2.5 rounded-md text-sm font-medium transition-all
                  bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Continue to sign in
              </button>
            </div>
          </div>
        </main>
      </div>
    )
  }

  // Password form
  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Logo - top left */}
      <a
        href="https://theseus.us"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed top-4 left-4 md:top-6 md:left-6 hover:opacity-80 transition-opacity z-10"
      >
        <img src={theseusLogo} alt="Theseus" className="h-5 md:h-6" />
      </a>

      {/* Centered content */}
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-10 wizard-fade-in">
            <h1 className="text-2xl text-foreground">
              Set new password
            </h1>
            <p className="text-muted-foreground mt-2">
              Enter your new password below.
            </p>
          </div>

          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }} className="space-y-6">
            <div style={{ animationDelay: '50ms' }} className="space-y-2 wizard-stagger">
              <label className="block text-sm text-muted-foreground/80">
                New password
              </label>
              <div className="relative">
                <input
                  className="ph-no-capture w-full bg-transparent border-b border-border/40 pb-2 pr-10 text-foreground text-base
                    placeholder:text-muted-foreground/40 focus:outline-none focus:border-foreground/60
                    transition-colors"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="At least 8 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      focusConfirmPassword()
                    }
                  }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-0 p-1 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div style={{ animationDelay: '100ms' }} className="space-y-2 wizard-stagger">
              <label className="block text-sm text-muted-foreground/80">
                Confirm password
              </label>
              <div className="relative">
                <input
                  ref={confirmPasswordRef}
                  className="ph-no-capture w-full bg-transparent border-b border-border/40 pb-2 pr-10 text-foreground text-base
                    placeholder:text-muted-foreground/40 focus:outline-none focus:border-foreground/60
                    transition-colors"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canSubmit) {
                      e.preventDefault()
                      handleSubmit()
                    }
                  }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-0 top-0 p-1 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive wizard-fade-in">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="w-full px-6 py-2.5 rounded-md text-sm font-medium transition-all
                  bg-primary text-primary-foreground hover:bg-primary/90
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Updating password...
                  </span>
                ) : (
                  <>
                    Update password
                    <span className="text-primary-foreground/60 ml-2 text-xs">↵</span>
                  </>
                )}
              </button>
            </div>
          </form>

          {/* Keyboard hint */}
          <div className="mt-8 text-center text-[10px] text-muted-foreground/40">
            <span className="px-1.5 py-0.5 bg-secondary/50 rounded">Enter</span> to continue
          </div>
        </div>
      </main>
    </div>
  )
}
